import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

//import theme from '@ecp/common/src/style/theme/default';
import boTheme from '@ecp/common/src/style/theme/bo';
import { boScrollCss, textEllipsis } from '@ecp/common/src/style/theme/defaultStyled';
import { FlexBox, FlexBoxColumn, HorizontalDivider } from '@ecp/common/src/layouts/flex/styled';
import {
  StyledCollapseArrow,
  StyledDropdownMenu,
  StyledFloatingRootComponent,
} from '@ecp/common/src/components/dropdown/bo/DropdownMenu';
import Floating from '@ecp/common/src/components/floating/bo/Floating';
import LabeledCheckBox from '@ecp/common/src/components/checkbox/bo/LabeledCheckBox';
import Button from '@ecp/common/src/components/button/bo/Button';
import CheckboxGroup from '@ecp/common/src/components/checkbox/bo/CheckboxGroup';
import useCheckboxGroupDropdown from '@ecp/common/src/components/checkboxGroupDropdown/useCheckboxGroupDropdown';
import { StyledErrorMessage } from '@ecp/common/src/components/input/bo/Input';

const StyledCheckboxGroupRoot = styled(FlexBoxColumn)`
  max-height: 136px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  align-items: flex-start;
  gap: 0;

  ${boScrollCss};
`;

const StyledLabeledCheckBox = styled(LabeledCheckBox)`
  background-color: ${boTheme.color.sub10};
  color: ${boTheme.color.sub1};
  font-size: ${boTheme.font.size.small};
  cursor: pointer;
  padding: 0 8px;

  &:hover:not([disabled]) {
    background-color: ${boTheme.color.table4};
  }

  &[disabled] {
    background-color: ${boTheme.color.sub8};
    color: ${boTheme.color.sub5};
    cursor: not-allowed;
  }
`;

const labelProps = {
  size: `${(props) => props.theme.font.size.small}`,
  color: `${(props) => props.theme.color.text.sub}`,
  height: '34px',
  width: '164px',
  style: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '158px',
    display: 'block',
  },
};

const AllCheckboxGroupMenu = ({
  readOnly,
  allChecked,
  hideAllCheck,
  onAllCheckChange,
  itemList,
  onCheckboxChange,
  onConfirm,
  onCancel,
  ...props
}) => {
  return (
    <FlexBoxColumn align-items={'flex-start'} width={'100%'}>
      {!hideAllCheck && (
        <StyledLabeledCheckBox
          label={'전체'}
          checked={allChecked}
          onChange={onAllCheckChange}
          rootProps={{ gap: '6px' }}
          labelProps={labelProps}
          width={'100%'}
          {...props}
        />
      )}
      <CheckboxGroup
        direction={'column'}
        rootComponent={StyledCheckboxGroupRoot}
        itemComponent={StyledLabeledCheckBox}
        itemList={itemList}
        onChange={onCheckboxChange}
        itemProps={{ gap: '6px' }}
        labelProps={labelProps}
        width={'100%'}
        {...props}
      />
      <HorizontalDivider />
      <FlexBox gap={'6px'} padding={'10px 0'} width={'100%'} justify-content={'center'}>
        <Button type={'Third'} width={'60px'} onClick={onCancel} disabled={readOnly}>
          취소
        </Button>
        <Button type={'Secondary'} width={'60px'} onClick={onConfirm} data-key={props['data-key']} disabled={readOnly}>
          확인
        </Button>
      </FlexBox>
    </FlexBoxColumn>
  );
};

AllCheckboxGroupMenu.propTypes = {
  readOnly: PropTypes.bool,
  allChecked: PropTypes.bool,
  onAllCheckChange: PropTypes.func,
  itemList: PropTypes.array,
  onCheckboxChange: PropTypes.func,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  hideAllCheck: PropTypes.bool,
  'data-key': PropTypes.string,
};

const StyledLabelWrapper = styled(FlexBox).attrs(({ width, style }) => ({
  style: { ...style, maxWidth: `calc(${width} - 32px)` },
}))``;

const StyledLabel = styled.div`
  ${textEllipsis};
`;

const StyledCount = styled.div`
  white-space: nowrap;
  padding-left: 3px;
`;

const CheckboxGroupDropdown = ({
  readOnly,
  itemList,
  onChange,
  disabled,
  width,
  hideAllCheck,
  limit,
  alert,
  error,
  errorMessage,
  ...props
}) => {
  const {
    open,
    currenItemList,
    allChecked,
    handleAllCheckChange,
    handleCheckboxChange,
    handleSetOpen,
    handleConfirm,
    handleCancel,
    checkedItemLabelList,
  } = useCheckboxGroupDropdown(itemList, onChange, limit, alert);

  return (
    <FlexBoxColumn align-items={'flex-start'}>
      <Floating
        value={
          <AllCheckboxGroupMenu
            readOnly={readOnly}
            allChecked={allChecked}
            hideAllCheck={hideAllCheck}
            onAllCheckChange={handleAllCheckChange}
            itemList={currenItemList}
            onCheckboxChange={handleCheckboxChange}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            {...props}
          />
        }
        openWhenClicked={!disabled && true}
        closeWhenOutside={!disabled && true}
        placement={'bottom'}
        open={open}
        onOpenChange={handleSetOpen}
        rootComponent={StyledFloatingRootComponent}
        rootProps={{ width }}
        {...props}
      >
        <StyledDropdownMenu
          width={width}
          height={'28px'}
          open={open}
          disabled={disabled}
          error={checkedItemLabelList.at(0) === '선택' && error}
          gap={'4px'}
        >
          <StyledLabelWrapper width={width} title={checkedItemLabelList.join()}>
            <StyledLabel>{checkedItemLabelList[0]}</StyledLabel>
            {checkedItemLabelList.length > 1 && <StyledCount>외 {checkedItemLabelList.length - 1}건</StyledCount>}
          </StyledLabelWrapper>
          <StyledCollapseArrow open={open} />
        </StyledDropdownMenu>
      </Floating>
      {checkedItemLabelList.at(0) === '선택' && error && errorMessage && (
        <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
      )}
    </FlexBoxColumn>
  );
};

CheckboxGroupDropdown.propTypes = {
  readOnly: PropTypes.bool,
  itemList: PropTypes.array,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  width: PropTypes.string,
  hideAllCheck: PropTypes.bool,
  limit: PropTypes.number,
  alert: PropTypes.func,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
};

CheckboxGroupDropdown.defaultProps = {
  width: '200px',
  limit: 0,
  readOnly: false,
};

export default CheckboxGroupDropdown;
